<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <VaAvatar :size="32" color="warning">
              {{ userInitials }}
            </VaAvatar>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <!-- User Info Section -->
        <div v-if="authStore.user" class="px-4 py-3 border-b border-[var(--va-background-border)]">
          <div class="flex items-center gap-3 mb-2">
            <VaAvatar :size="40" color="primary">
              {{ userInitials }}
            </VaAvatar>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-base truncate">{{ authStore.user.name }}</p>
              <p class="text-sm text-[var(--va-secondary)] truncate">{{ authStore.user.email }}</p>
              <p v-if="authStore.user.role" class="text-xs text-[var(--va-secondary)] mt-1">
                {{ authStore.user.role }}
              </p>
            </div>
          </div>
        </div>

        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleItemClick(item)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useColors } from 'vuestic-ui'
import { useAuthStore } from '../../../../stores/auth-store'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

const userInitials = computed(() => {
  if (!authStore.user?.name) {
    return '?'
  }
  const names = authStore.user.name.trim().split(' ')
  if (names.length >= 2) {
    return (names[0][0] + names[names.length - 1][0]).toUpperCase()
  }
  return authStore.user.name.substring(0, 2).toUpperCase()
})

onMounted(async () => {
  // Fetch user details if authenticated but user data is incomplete
  if (authStore.isAuthenticated && (!authStore.user?.id || !authStore.user?.role)) {
    try {
      await authStore.fetchUserDetails()
    } catch {
      // User details fetch failed; UI will show incomplete state
    }
  }
})

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  action?: () => void
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const handleItemClick = (item: ProfileListItem) => {
  if (item.action) {
    item.action()
  }
}

const props = defineProps<{
  options?: ProfileOptions[]
}>()

const defaultOptions = computed<ProfileOptions[]>(() => [
  {
    name: '',
    separator: false,
    list: [
      {
        name: 'changePassword',
        icon: 'mso-lock',
        to: 'change-password',
      },
    ],
  },
  {
    name: '',
    separator: true,
    list: [
      {
        name: 'logout',
        icon: 'mso-logout',
        action: () => {
          authStore.clearAuth()
          router.push({ name: 'login' })
        },
      },
    ],
  },
])

const options = computed(() => props.options || defaultOptions.value)

const isShown = ref(false)

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.action) {
    return {}
  }
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
